import { lazy } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { seoHead } from "@/config/seoHead";
import { SITE_URL } from "@/config/urlHierarchy";
import { pickPageFields, scrapedListQueryOptions, type ScrapedPage } from "@/hooks/useScrapedPage";
import { BLOG_PAGE_SIZE, pageCount, parsePageParam } from "@/lib/pagination";

const BloggLista = lazy(() => import("@/pages/content/BloggLista"));

const DESCRIPTION = "Senaste artiklarna om elmarknaden, elpriser, solceller och energi.";

export const Route = createFileRoute("/blogg/")({
  // ?sida=2 … – varje sida är självkanonisk och länkar vidare med vanliga
  // <a href>, så att alla artiklar nås från serverns HTML.
  validateSearch: (search: Record<string, unknown>): { sida?: number } => {
    const sida = parsePageParam(search["sida"]);
    return sida > 1 ? { sida } : {};
  },
  // Routern behåller okända/ogiltiga råvärden i search – tolka därför om här.
  loaderDeps: ({ search }) => ({ sida: parsePageParam(search.sida) }),
  loader: async ({ context, deps }) => {
    let items: Partial<ScrapedPage>[] | null = null;
    try {
      const list = await context.queryClient.ensureQueryData(scrapedListQueryOptions("post", "published_at"));
      // Bara fälten som korten visar – loaderdatan hamnar i HTML:en.
      items = list.map((i) => ({
        ...pickPageFields(i, ["id", "slug", "path", "title", "hero_image", "published_at"]),
        ...(i.meta_description ? { meta_description: i.meta_description.slice(0, 220) } : {}),
      }));
    } catch (err) {
      console.error("Blogglistan kunde inte hämtas i loadern", err);
    }
    // Sidnummer bortom sista sidan finns inte (riktig 404, inte en tom lista).
    if (items && deps.sida > pageCount(items.length, BLOG_PAGE_SIZE)) throw notFound();
    return { items, page: deps.sida };
  },
  head: ({ loaderData }) => {
    const page = loaderData?.page ?? 1;
    const head = seoHead("/blogg", { description: DESCRIPTION });
    if (page <= 1) return head;
    const url = `${SITE_URL}/blogg?sida=${page}`;
    const suffix = ` – sida ${page}`;
    return {
      meta: head.meta.map((m) => {
        if ("title" in m && m.title) return { title: m.title.replace(/( \| Elmarknad\.se)?$/, `${suffix}$1`) };
        if ("property" in m && m.property === "og:url") return { ...m, content: url };
        return m;
      }),
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BloggRoute,
});

function BloggRoute() {
  const { items, page } = Route.useLoaderData();
  return <BloggLista initialItems={items} page={page} />;
}
